import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Caption1,
  Checkbox,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Select,
  makeStyles,
} from '@fluentui/react-components';
import {
  MAX_STATUS_NOTE_LENGTH, activationChangesReadiness, activationIssues, activationOfferSource, activationPatch, allowedTransitions, canTransition, effectiveOnSchema, fillActivationDetails,
  lifecycleStateSchema, reasonChoicesFor, reasonQuestions, returnsWithoutActivationCheck, statusReasonLabels,
  type ActivationDetails, type LifecycleState, type Person, type ReasonState, type StatusReason,
} from '@vigeo/shared';
import { tokens, shellMedia, shellMetrics } from '@vigeo/ui';
import { fetchHrWorkflow } from '@/features/hr-workflow';
import { changeLifecycle, fetchChecklist, updatePerson, type ApiError } from '@/lib/api';
import { ActivationDetailsFields } from './ActivationDetailsFields.js';
import { lifecyclePresenters } from './lifecycle.js';

const useStyles = makeStyles({
  // On a phone the dropdown's button is the touch target, so it takes the accessible minimum (Gate 6).
  dropdown: { [shellMedia.phone]: { '& .fui-Dropdown__button': { minHeight: shellMetrics.touchTarget } } },
  control: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, maxWidth: '28rem' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  optionLine: { display: 'flex', flexDirection: 'column' },
  /** Paul, 2026-09-29: "Withdrawn (in red)". */
  leaving: { color: tokens.colorPaletteRedForeground1 },
});

/** Every stage, in Paul's order, so a disallowed one can still be shown and explained. */
const ALL_STAGES: readonly LifecycleState[] = lifecycleStateSchema.options;

/**
 * Moves that belong to a DEDICATED FLOW, so this control must not offer them.
 *
 * `candidate -> onboarding` is a legal domain transition — `allowedTransitions` is right to allow it
 * and the API is right to accept it — but Paul (2026-09-29 17:49) put it on the Send Onboarding Items
 * path, which has its own three frames (2.2-2.4, #373). Offering it here would be a second way to do
 * something that already has a designed flow, and taking it out of `allowedTransitions` instead would
 * break the flow that actually performs it.
 *
 * So the difference between "legal" and "offered here" is deliberate. Do not collapse them.
 */
const MOVES_WITH_THEIR_OWN_FLOW: ReadonlyArray<readonly [LifecycleState, LifecycleState]> = [
  ['candidate', 'onboarding'],
];

function offeredFrom(person: Person): readonly LifecycleState[] {
  const from = person.lifecycle;
  // Staff added by hand may go Inactive again: they come back without the activation check (ADR-016 Amendment 2, #810).
  return allowedTransitions(from).filter((to) => !MOVES_WITH_THEIR_OWN_FLOW.some(([f, t]) => f === from && t === to));
}

/**
 * Why a stage cannot be picked right now, DERIVED from the transition table rather than written out.
 *
 * Paul's examples were "Only from Active" and "Only from Active or Inactive", which this reproduces.
 * For Candidate seen from Onboarding he wrote "Not available from Onboarding"; this says "Only from
 * Separated or Withdrawn", which is true and more use to the reader. Flagged on #388 rather than
 * special-cased, so he can correct the wording in one place if he wants his phrasing.
 */
function onlyFrom(to: LifecycleState): string {
  const sources = ALL_STAGES.filter((from) => canTransition(from, to));
  return sources.length === 0
    ? 'Not reachable by a stage change'
    : `Only from ${sources.map((from) => lifecyclePresenters[from].label).join(' or ')}`;
}

export interface LifecycleControlProps {
  readonly person: Person;
  /** Write actions are HR_Admin only; the API enforces it too. */
  readonly canEdit: boolean;
  /**
   * Closes the control without changing anything (Paul's Cancel). Optional because the directory
   * renders this inline with nothing to close — a Cancel there would have nothing to do.
   */
  readonly onCancel?: () => void;
}

/**
 * Moving a person through the pipeline.
 *
 * Only LEGAL destinations are offered, read from `allowedTransitions` in packages/shared —
 * the same function the API guards with, so the UI cannot offer a move the server will
 * refuse. A reason is required because an unexplained state change is not reviewable, and
 * it lands on the audit event.
 */
export function LifecycleControl({ person, canEdit, onCancel }: LifecycleControlProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [to, setTo] = useState<LifecycleState | ''>('');
  const [reason, setReason] = useState('');
  // The closed reason, its optional note and the optional effective date (#772). The reason is asked only for Inactive and Separated.
  const [statusReason, setStatusReason] = useState<StatusReason | ''>('');
  const [statusNote, setStatusNote] = useState('');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  // Entering Active with the activation check. A person added by hand coming back from leave skips it (ADR-016 Amendment 2, #810),
  // so nothing about activation is read, asked or saved for them.
  const activating = to === 'active' && !returnsWithoutActivationCheck(person, 'active');
  const readiness = useQuery({ queryKey: ['checklist', person.id], queryFn: () => fetchChecklist(person.id), enabled: canEdit && activating });
  // The signed offer behind the fill (#764), read only while HR is making someone Active. The key is the one the person page already reads.
  const workflow = useQuery({ queryKey: ['hr-workflow', person.id], queryFn: () => fetchHrWorkflow(person.id), enabled: canEdit && activating });
  const source = workflow.data === undefined ? ({ kind: 'none' } as const) : activationOfferSource(workflow.data);
  const fill = fillActivationDetails(person, source);
  // What HR changed in the details, over the fill. Derived, never stored: the record is written only on save.
  const [edits, setEdits] = useState<Partial<ActivationDetails>>({});
  const details: ActivationDetails = { ...fill.details, ...edits };
  // The sparse patch the save would send; null when a value is refused by the shared schema.
  const patch = (() => { try { return activating ? activationPatch(person, details) : null; } catch { return null; } })();
  // The readiness on screen was worked out from the record BEFORE this save. When the save changes the W-2 or 1099
  // (which decides what applies), that answer can be wrong in both directions, so the server decides after the update
  // and the dialog does not hold the move back on it.
  const readinessRechecked = patch !== null && activationChangesReadiness(patch);
  const blockers = activating ? activationIssues(details.firstDay === '' ? null : `${details.firstDay}T00:00:00.000Z`, readinessRechecked ? 'ready' : readiness.data?.state, confirmed) : [];
  /** The update went through and the move after it did not, so HR is told both. */
  const [detailsSaved, setDetailsSaved] = useState(false);

  const offered = offeredFrom(person);
  const choices = to === '' ? [] : reasonChoicesFor(to);
  const picksReason = choices.length > 0;
  const typedDate = effectiveOn.trim();
  const dateProblem = typedDate !== '' && !effectiveOnSchema.safeParse(typedDate).success ? 'Enter a real date.' : null;
  // What the audit row's free-text reason says. For a move that carries a reason it is the picked words ALONE: the note can hold
  // personal detail and is kept on the person's record only (the server writes the label whatever it is sent).
  const reasonText = picksReason ? (statusReason === '' ? '' : statusReasonLabels[statusReason]) : reason;

  const move = useMutation({
    mutationFn: async () => {
      if (activating) {
        // The details HR confirmed go first, through the ordinary audited person update (no new write path),
        // so the readiness the server derives for the move already reads the confirmed W-2 or 1099.
        setDetailsSaved(false);
        if (patch === null) throw new Error('One of the employment details is not valid. Check each value.');
        if (Object.keys(patch).length > 0) { await updatePerson(person.id, patch); setDetailsSaved(true); }
      }
      return changeLifecycle(person.id, {
        to: to as LifecycleState,
        reason: reasonText,
        confirmFinalHrClearance: confirmed,
        ...(picksReason && statusReason !== '' ? { statusReason, ...(statusNote.trim() === '' ? {} : { statusNote: statusNote.trim() }) } : {}),
        ...(typedDate === '' ? {} : { effectiveOn: typedDate }),
      });
    },
    // A refused move can follow a saved update: show the record, and the readiness read from it, as they now are.
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['person', person.id] });
      await queryClient.invalidateQueries({ queryKey: ['checklist', person.id] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['person', person.id] });
      await queryClient.invalidateQueries({ queryKey: ['checklist', person.id] });
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      setTo('');
      setReason('');
      setStatusReason('');
      setStatusNote('');
      setEffectiveOn('');
      setConfirmed(false);
      setEdits({});
      setDetailsSaved(false);
    },
  });

  if (!canEdit) return null;

  if (offered.length === 0) {
    return <Caption1>No further stages are available from {lifecyclePresenters[person.lifecycle].label}.</Caption1>;
  }

  const error = move.error as (Error & Partial<ApiError>) | null;

  return (
    <div className={styles.control}>
      {/*
        EVERY STAGE IS LISTED, and the ones that cannot be picked say why (Paul, 2026-09-29 17:49).
        A stage that is simply absent leaves HR wondering whether it exists; "Only from Active" answers
        the question they actually have. The current stage is shown and marked rather than hidden, so
        the list reads as the whole set of stages rather than a menu of leftovers.
      */}
      <Field label="Stage" hint="The current stage is marked. Stages that cannot be reached from here say why.">
        <Dropdown
          className={styles.dropdown}
          value={to === '' ? '' : lifecyclePresenters[to].label}
          selectedOptions={to === '' ? [] : [to]}
          onOptionSelect={(_, d) => { setTo((d.optionValue ?? '') as LifecycleState | ''); setStatusReason(''); }}
        >
          {ALL_STAGES.map((state) => {
            const current = state === person.lifecycle;
            const selectable = offered.includes(state);
            const note = current ? 'Current stage' : selectable ? lifecyclePresenters[state].description
              : onlyFrom(state);
            return (
              <Option
                key={state}
                value={state}
                // `text` is what the closed dropdown shows and what a name-based query matches, so it
                // stays the bare label; the reason lives in the option's own content.
                text={lifecyclePresenters[state].label}
                /*
                  An explicit hook, because each option's accessible name now CONTAINS its reason —
                  and "Only from Active or Inactive" on Separated matches a search for Inactive. A
                  test that picks options out of prose picks the wrong one.
                */
                data-stage={state}
                disabled={!selectable}
                className={state === 'withdrawn' ? styles.leaving : undefined}
              >
                <span className={styles.optionLine}>
                  <span>{current ? `${lifecyclePresenters[state].label} \u2713` : lifecyclePresenters[state].label}</span>
                  <Caption1>{note}</Caption1>
                </span>
              </Option>
            );
          })}
        </Dropdown>
      </Field>

      {picksReason ? <>
        <Field label={reasonQuestions[to as ReasonState]} required>
          <Select value={statusReason} onChange={(_, d) => setStatusReason(d.value as StatusReason | '')}>
            <option value="">Choose a reason</option>
            {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.label}</option>)}
          </Select>
        </Field>
        <Field label="Note (optional)" hint="Kept on this person's record. It is not copied to the audit trail.">
          <Input value={statusNote} maxLength={MAX_STATUS_NOTE_LENGTH} onChange={(_, d) => setStatusNote(d.value)} />
        </Field>
      </> : (
        <Field label="Reason" required hint="Recorded on the audit trail.">
          <Input value={reason} onChange={(_, d) => setReason(d.value)} />
        </Field>
      )}
      {to === '' ? null : (
        <Field label="Date it took effect (optional)" hint="Leave blank if it took effect when you record it. The time you record it is kept either way." validationMessage={dateProblem ?? undefined}>
          <Input type="date" value={effectiveOn} onChange={(_, d) => setEffectiveOn(d.value)} />
        </Field>
      )}
      {activating ? <>
        <ActivationDetailsFields
          values={details}
          fill={fill}
          source={source}
          reading={workflow.isPending && workflow.fetchStatus !== 'idle'}
          unreadable={workflow.isError}
          disabled={move.isPending}
          onChange={(change) => setEdits((current) => ({ ...current, ...change }))}
        />
        <Checkbox checked={confirmed} label="I confirm final HR clearance and that the employee may begin work." onChange={(_, d) => setConfirmed(d.checked === true)} />
        {readinessRechecked && readiness.data?.state !== 'ready'
          ? <Caption1>Whether every requirement is ready is checked again when these details are saved.</Caption1> : null}
        {blockers.map((message) => <Caption1 key={message}>{message}</Caption1>)}
      </> : null}

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{detailsSaved ? 'Your employment details were saved, but the stage was not changed. ' : ''}{error.message}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actions}>
        {onCancel === undefined ? null : (
          <Button appearance="secondary" disabled={move.isPending} onClick={onCancel}>
            Cancel
          </Button>
        )}
        {/*
          NAMED WITH ITS DESTINATION, because the header badge that OPENS this control is itself
          named "Change stage: <current stage>" (PersonPage). Two buttons whose accessible names both
          begin "Change stage" are confusable by anyone listening rather than looking, and a test
          caught the ambiguity as soon as the visible label changed.

          The visible text stays exactly what Paul specified, and the accessible name CONTAINS it, as
          WCAG 2.5.3 requires — it only adds where the move goes.
        */}
        <Button
          appearance="primary"
          aria-label={to === '' ? undefined : `Change stage to ${lifecyclePresenters[to].label}`}
          disabled={to === '' || reasonText.trim() === '' || dateProblem !== null || move.isPending || blockers.length > 0}
          onClick={() => move.mutate()}
        >
          Change stage
        </Button>
        {to === '' ? null : <Caption1>{lifecyclePresenters[to].description}</Caption1>}
      </div>
    </div>
  );
}
